/**
 * LoggingContext - React Context for easy access to logging throughout the app
 * 
 * Provides hooks and context for logging user actions
 * Wraps the LoggingService with React-friendly API
 */

import React, { createContext, useContext, useCallback } from 'react';
import { loggingService } from '../services/loggingService';
import { LogAction } from '../utils/logging/actions';

interface LoggingContextType {
  log: (action: LogAction) => Promise<void>;
  logScreenView: (screenName: string, params?: Record<string, any>) => Promise<void>;
  logBookOpen: (bookId: string, bookTitle?: string, source?: string) => Promise<void>;
  logChapterOpen: (bookId: string, chapterId: string, chapterTitle?: string) => Promise<void>;
  logButtonPress: (buttonId: string, context?: Record<string, any>) => Promise<void>;
}

const LoggingContext = createContext<LoggingContextType | undefined>(undefined);

export const useLogging = () => {
  const context = useContext(LoggingContext);
  if (!context) {
    throw new Error('useLogging must be used within a LoggingProvider');
  }
  return context;
};

interface LoggingProviderProps {
  children: React.ReactNode;
}

export const LoggingProvider: React.FC<LoggingProviderProps> = ({ children }) => {
  const log = useCallback(async (action: LogAction) => {
    await loggingService.log(action);
  }, []);

  const logScreenView = useCallback(async (screenName: string, params?: Record<string, any>) => {
    await loggingService.logScreenView(screenName, params);
  }, []);

  const logBookOpen = useCallback(async (bookId: string, bookTitle?: string, source?: string) => {
    await loggingService.logBookOpen(bookId, bookTitle, source);
  }, []);

  const logChapterOpen = useCallback(async (bookId: string, chapterId: string, chapterTitle?: string) => {
    await loggingService.logChapterOpen(bookId, chapterId, chapterTitle);
  }, []);

  const logButtonPress = useCallback(async (buttonId: string, context?: Record<string, any>) => {
    await loggingService.logButtonPress(buttonId, context);
  }, []);

  const value: LoggingContextType = {
    log,
    logScreenView,
    logBookOpen,
    logChapterOpen,
    logButtonPress,
  };

  return (
    <LoggingContext.Provider value={value}>
      {children}
    </LoggingContext.Provider>
  );
};

